import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Loader2, Plus, X } from 'lucide-react'
import { Badge } from '../Badge/Badge'
import { cn, getFriendlyErrorMessage, interpolateTemplate, normalizeFilters } from './utils'
import type { FilterExpression } from './types/filter.types'
import {
  useClickOutside,
  useDebouncedSearch,
  useFloatingDropdown,
  useKeyboardNavigation,
  useTranslationSafe,
  useVirtualList,
} from './hooks'
import { SearchInput } from './parts/SearchInput'
import { LiveRegion } from './parts/LiveRegion'
import { CreatePopup } from './parts/CreatePopup'
import type { LookupFieldProps, LookupFieldRef, LookupRecord } from './LookupField.types'
import type { SearchOperator } from './services/api.types'
import './LookupField.css'

const DEFAULT_PAGE_SIZE = 10
const SCROLL_THRESHOLD_PX = 50
const DEFAULT_MIN_CHARS = 1
// Multi-select trigger: chips beyond this count collapse into a single "+N" badge so
// the trigger doesn't grow without bound as more records get selected.
const MAX_VISIBLE_CHIPS = 3

function LookupFieldInner<TRecord extends LookupRecord = LookupRecord>(
  {
    entity,
    label,
    displayFields,
    value,
    onChange,
    fetcher,
    clearable = true,
    clearSelectionLabel = 'Clear selection',
    removeLabel = 'Remove',
    placeholder,
    className,
    pageSize = DEFAULT_PAGE_SIZE,
    onError,
    searchFields,
    minChars = DEFAULT_MIN_CHARS,
    displayValue,
    displayTemplate,
    renderSelected,
    filters,
    getFilters,
    dependencies,
    orderBy,
    bindFields,
    readOnly = false,
    disabled = false,
    error = false,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    onReady,
    onInvalidValue,
    noResultsText,
    searchPlaceholder,
    required = false,
    showRequiredIndicator,
    validateOnBlur = true,
    onValidationChange,
    errorText,
    onCreateNew,
    createVariant,
    createTitle,
    createButtonLabel,
    createIcon,
    showCreateButton: showCreateButtonProp,
    createWidth,
    createHeight,
    createShowHeader,
    isMultiple,
    maxSelected,
    onMaxSelectedReached,
  }: LookupFieldProps<TRecord>,
  ref: React.Ref<LookupFieldRef<TRecord>>
) {
  const { t } = useTranslationSafe()

  // Stable value-based keys for array/object props that hosts routinely pass as fresh
  // literals every render (e.g. displayFields={['Code', 'Name']}) — comparing by
  // content (JSON.stringify) instead of by reference in the memoization deps below
  // means an unrelated parent re-render doesn't recreate fetchRecords/fetchMoreRecords,
  // which would otherwise re-trigger the fetch-on-open effect and silently refetch page
  // 1 (discarding scroll position and loaded pages) on every incidental re-render while
  // the dropdown is open. Same technique already used for the `dependencies` prop above.
  const displayFieldsKey = JSON.stringify(displayFields)
  const bindFieldsKey = JSON.stringify(bindFields ?? [])
  const searchFieldsKey = JSON.stringify(searchFields ?? [])
  // filtersKey is declared once here and reused both for fetch memoization (below) and
  // for the cache-invalidation-on-filters-change effect further down.
  const filtersKey = JSON.stringify(filters ?? [])
  const orderByKey = JSON.stringify(orderBy ?? null)

  // Compute effective orderBy with default (AC: 2)
  const effectiveOrderBy = useMemo(
    () =>
      orderBy ?? {
        field: displayFields[0] as string,
        direction: 'asc' as const,
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderByKey, displayFieldsKey]
  )

  const id = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState<Error | null>(null)
  const [records, setRecords] = useState<TRecord[]>([])
  // Source of truth for BOTH modes — single-select never holds more than one entry.
  // `selectedRecord` below is a derived convenience so the (much larger) single-select
  // code paths below don't need touching at all.
  const [selectedRecords, setSelectedRecords] = useState<TRecord[]>([])
  const selectedRecord = selectedRecords[0] ?? null
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const previousRecordRef = useRef<TRecord | null>(null)
  const previousDependenciesRef = useRef(JSON.stringify(dependencies ?? []))
  const optionsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  // A <div role="button"> in multi-select mode (see the trigger render below for why:
  // it hosts real <button> chip-remove controls, which can't legally nest inside
  // another <button>), so this ref must accept either element type.
  const triggerRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedSearch(searchQuery)
  // How the typed search text is matched — toggled via the icons inside the search box.
  // Always starts as 'startsWith'; the user can switch to 'contains' but there's no third
  // state to cycle back through, so it never defaults to anything else.
  const [searchOperator, setSearchOperator] = useState<SearchOperator>('startsWith')
  const [announcement, setAnnouncement] = useState('')
  // Enter-to-resolve-exact-code: pressing Enter with nothing highlighted (no arrow-key
  // navigation yet, and no single result auto-highlighted either) tries to resolve the
  // typed text as an exact code match, bypassing startsWith/contains guessing. 'searching'
  // shows a spinner in the search box. When it comes back empty, nothing visible happens
  // (no red banner) — only an aria-live announcement for screen readers.
  const [exactMatchStatus, setExactMatchStatus] = useState<'idle' | 'searching'>('idle')
  const isResolvingExactMatchRef = useRef(false)

  // Refs for onReady callback (Task 6) [UPDATED v0.4 - removed totalRecordsRef]
  const hasCalledOnReadyRef = useRef(false)

  // Ref to track selectedRecord for imperative methods (always up-to-date)
  const selectedRecordRef = useRef<TRecord | null>(null)
  // Keep ref in sync with state
  selectedRecordRef.current = selectedRecord
  // Multi-select's own ref — mirrors selectedRecords for the toggle/clear/value-sync
  // logic below that needs the full array, not just the first entry.
  const selectedRecordsRef = useRef<TRecord[]>([])
  selectedRecordsRef.current = selectedRecords

  // Refs for focus tracking (Tasks 4, 5)
  const componentRef = useRef<HTMLDivElement>(null)
  const isFocusWithinRef = useRef(false)
  const onInvalidValueRef = useRef(onInvalidValue)

  // Keep onInvalidValue ref in sync
  useEffect(() => {
    onInvalidValueRef.current = onInvalidValue
  }, [onInvalidValue])

  // Floating UI positioning (AC: 8, Story 2.11)
  const { refs: floatingRefs, floatingStyles } = useFloatingDropdown({
    enabled: isOpen,
  })

  // Click outside detection - close dropdown when clicking outside (AC: 4, Story 2.11)
  // Note: mousedown fires BEFORE focus changes, so we need to manually trigger onBlur
  // if focus was within the component when clicking outside
  const handleClickOutside = useCallback(() => {
    if (isOpen) {
      // Trigger onBlur if focus was within (mousedown preempts natural blur)
      if (isFocusWithinRef.current) {
        isFocusWithinRef.current = false
        onBlurProp?.(selectedRecordRef.current)
      }
      setIsOpen(false)
      setSearchQuery('')
    }
  }, [isOpen, onBlurProp])

  // Exclude the portaled floating dropdown from outside-click detection
  useClickOutside(componentRef, handleClickOutside, isOpen, [floatingRefs.floating])

  // Multi-select: already-picked records always show first in the dropdown, so the
  // user can see (and un-pick) their current selection without hunting for it further
  // down the list. Single-pass stable partition — keeps each group's own relative
  // order, cheaper than a full sort. This is a RENDER-ONLY reordering: `records`
  // itself (fetch/pagination order) never changes, only what's displayed does —
  // keyboard nav and virtualization both key off this same reordered view below so
  // "highlighted" and "visually at this position" never disagree.
  const displayRecords = useMemo(() => {
    if (!isMultiple || selectedRecords.length === 0) return records
    const selectedIds = new Set(selectedRecords.map((r) => r.Id))
    const selected: TRecord[] = []
    const rest: TRecord[] = []
    for (const r of records) {
      if (selectedIds.has(r.Id)) selected.push(r)
      else rest.push(r)
    }
    return [...selected, ...rest]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, isMultiple, selectedRecords])

  // Virtualization for large lists (Story 2.13, AC: 1, 8)
  const { virtualItems, totalSize } = useVirtualList({
    count: displayRecords.length,
    parentRef: optionsRef,
    enabled: isOpen && displayRecords.length > 0,
  })

  // State for invalid value detection (Task 7)
  const [isInvalidValue, setIsInvalidValue] = useState(false)

  // State for required field validation (Story 2.12)
  const [validationError, setValidationError] = useState(false)

  // Navigation context cache state (Story 2.13, AC: 2, 7)
  const [cachedRecords, setCachedRecords] = useState<TRecord[]>([])
  const [cachedScrollPosition, setCachedScrollPosition] = useState<number>(0)
  const [cachedCurrentPage, setCachedCurrentPage] = useState<number>(1)
  const [cacheValid, setCacheValid] = useState<boolean>(false)
  const cachedSearchQueryRef = useRef<string>('')
  // Ref to prevent fetchMoreRecords during cache restoration (fixes duplicate records bug)
  const isRestoringCacheRef = useRef<boolean>(false)
  // Ref to track current page immediately (state updates are async)
  const currentPageRef = useRef<number>(1)

  // Compute combined error state: external error OR internal validation error
  const showError = error || validationError

  // The "+" button renders only when onCreateNew is provided AND not explicitly
  // hidden — lets a host keep the create props configured while suppressing the
  // affordance for a given instance (e.g. permission-gated creation).
  const showCreateButton = !!onCreateNew && showCreateButtonProp !== false

  // Compute effective showRequiredIndicator (defaults to required prop value)
  const effectiveShowRequiredIndicator = showRequiredIndicator ?? required

  // Ref for onValidationChange to avoid stale closures
  const onValidationChangeRef = useRef(onValidationChange)
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange
  }, [onValidationChange])

  // Compute effective filters (getFilters takes precedence over static filters)
  const getEffectiveFilters = useCallback((): FilterExpression | undefined => {
    if (getFilters) {
      return normalizeFilters(getFilters())
    }
    return normalizeFilters(filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, getFilters])

  const fetchRecords = useCallback(
    async (search: string = '') => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        setIsLoading(true)
        setApiError(null)
        setAnnouncement(t('announcements.loading'))

        const effectiveFilters = getEffectiveFilters()

        // Combine fields with deduplication: Id + displayFields + bindFields (PascalCase standard)
        const allFields = [...new Set(['Id', ...displayFields, ...(bindFields ?? [])])]

        const response = await fetcher<TRecord>(entity, {
          fields: allFields,
          search: search.length >= minChars ? search : '',
          searchFields: searchFields ?? displayFields,
          searchOperator,
          page: 1,
          pageSize,
          filters: effectiveFilters,
          orderBy: effectiveOrderBy,
        })

        // Check if request was aborted
        if (controller.signal.aborted) return

        setRecords(response.data)

        // Call onReady after first successful data load (AC: 4 - fires only once) [UPDATED v0.4 - removed totalRecords]
        if (!hasCalledOnReadyRef.current && onReady) {
          hasCalledOnReadyRef.current = true
          // Delay slightly to ensure imperativeRef is up to date
          setTimeout(() => {
            onReady({ firstPageLoaded: true }, imperativeRef)
          }, 0)
        }

        // Announce results loaded or no results (AC: 7)
        if (response.data.length === 0) {
          setAnnouncement(t('announcements.noResults'))
        } else {
          setAnnouncement(t('announcements.opened', { count: response.data.length }))
        }

        // Detect end of data on initial load (AC: 5)
        if (response.data.length < pageSize) {
          setHasMorePages(false)
        }
      } catch (err) {
        // Don't set error state for aborted requests
        if (err instanceof Error && err.name === 'AbortError') return

        const fetchError = err instanceof Error ? err : new Error('Unknown error')
        setApiError(fetchError)
        onError?.(fetchError)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    // Note: imperativeRef is accessed via closure, not dependency, to avoid circular dep.
    // Uses the stable *Key strings (not the raw arrays) so an equivalent-but-new-identity
    // displayFields/bindFields/searchFields prop doesn't recreate this callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entity,
      displayFieldsKey,
      bindFieldsKey,
      searchFieldsKey,
      searchOperator,
      pageSize,
      minChars,
      onError,
      onReady,
      getEffectiveFilters,
      effectiveOrderBy,
      fetcher,
    ]
  )

  const fetchMoreRecords = useCallback(
    async (search: string = '') => {
      // Skip if currently restoring cache (prevents duplicate records bug)
      if (isRestoringCacheRef.current) return
      if (isLoadingMore || !hasMorePages) return
      // Use ref instead of state to get current page (state updates are async)
      const actualCurrentPage = currentPageRef.current

      try {
        setIsLoadingMore(true)
        setAnnouncement(t('announcements.loadingMore'))

        const effectiveFilters = getEffectiveFilters()
        const nextPage = actualCurrentPage + 1

        // Combine fields with deduplication: Id + displayFields + bindFields (PascalCase standard)
        const allFields = [...new Set(['Id', ...displayFields, ...(bindFields ?? [])])]

        const response = await fetcher<TRecord>(entity, {
          fields: allFields,
          search: search.length >= minChars ? search : '',
          searchFields: searchFields ?? displayFields,
          searchOperator,
          page: nextPage,
          pageSize,
          filters: effectiveFilters,
          orderBy: effectiveOrderBy,
        })

        // Append new records to existing list (AC: 4)
        setRecords((prev) => [...prev, ...response.data])
        currentPageRef.current = nextPage
        setCurrentPage(nextPage)

        // Announce loaded count
        setAnnouncement(t('announcements.resultsLoaded', { count: response.data.length }))

        // Detect end of data (AC: 5)
        if (response.data.length < pageSize) {
          setHasMorePages(false)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        const error = err instanceof Error ? err : new Error('Unknown error')
        onError?.(error)
      } finally {
        setIsLoadingMore(false)
      }
    },
    // Uses the stable *Key strings (not the raw arrays) so an equivalent-but-new-identity
    // displayFields/bindFields/searchFields prop doesn't recreate this callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entity,
      displayFieldsKey,
      bindFieldsKey,
      searchFieldsKey,
      searchOperator,
      pageSize,
      minChars,
      // Note: currentPage removed - using currentPageRef instead for immediate updates
      isLoadingMore,
      hasMorePages,
      onError,
      getEffectiveFilters,
      effectiveOrderBy,
      fetcher,
      t,
    ]
  )

  // Helper to fetch a single record by ID (for setValue and invalid value detection)
  const fetchAndSetRecordById = useCallback(
    async (recordId: string): Promise<TRecord | null> => {
      try {
        const allFields = [...new Set(['Id', ...displayFields, ...(bindFields ?? [])])]
        const response = await fetcher<TRecord>(entity, {
          fields: allFields,
          filters: [[{ Id: recordId }]],
          page: 1,
          pageSize: 1,
        })
        return response.data[0] ?? null
      } catch {
        return null
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity, displayFieldsKey, bindFieldsKey, fetcher]
  )

  // Multi-select's batched equivalent of fetchAndSetRecordById — one request for the
  // whole `value` array via an OR filter group, instead of one request per id. Same
  // "unresolved id → silently omitted" contract, and same lack of getEffectiveFilters()
  // merge, as the single-record fetch above.
  const fetchRecordsByIds = useCallback(
    async (recordIds: string[]): Promise<TRecord[]> => {
      if (recordIds.length === 0) return []
      try {
        const allFields = [...new Set(['Id', ...displayFields, ...(bindFields ?? [])])]
        const response = await fetcher<TRecord>(entity, {
          fields: allFields,
          filters: [recordIds.map((id) => ({ Id: id }))],
          page: 1,
          pageSize: recordIds.length,
        })
        const byId = new Map(response.data.map((r) => [String(r.Id), r]))
        // Preserve the order of `value`; ids that no longer resolve are simply dropped.
        return recordIds.map((id) => byId.get(id)).filter((r): r is TRecord => !!r)
      } catch {
        return []
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity, displayFieldsKey, bindFieldsKey, fetcher]
  )

  // Validation function for required field (Story 2.12, AC: 4, 6, 10; Story 2.14, AC: 1)
  // Used by both blur validation and imperative validate() method
  const performValidation = useCallback((): boolean => {
    // Skip validation for disabled or readOnly - can't interact, so always valid
    if (disabled || readOnly) {
      setValidationError(false)
      onValidationChangeRef.current?.(true)
      return true
    }

    // Check if required and no selection (works for both modes: single-select never
    // holds more than one entry in selectedRecordsRef)
    if (required && selectedRecordsRef.current.length === 0) {
      setValidationError(true)
      onValidationChangeRef.current?.(false)
      return false
    }

    // Valid - clear any existing validation error
    setValidationError(false)
    onValidationChangeRef.current?.(true)
    return true
  }, [required, readOnly, disabled])

  const handleScroll = useCallback(() => {
    const container = optionsRef.current
    if (!container) return

    // Skip if already loading or no more pages
    if (isLoadingMore || !hasMorePages) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX

    if (isNearBottom) {
      fetchMoreRecords(debouncedSearchQuery)
    }
  }, [isLoadingMore, hasMorePages, fetchMoreRecords, debouncedSearchQuery])

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus({ preventScroll: true })
    }
  }, [isOpen])

  // Fetch when debounced search changes or dropdown opens (Story 2.13: cache restore)
  useEffect(() => {
    if (isOpen) {
      // Only fetch if search is empty or has >= minChars
      if (debouncedSearchQuery.length === 0 || debouncedSearchQuery.length >= minChars) {
        // Check if we can restore from cache (Story 2.13, AC: 3, 10)
        const canRestoreFromCache =
          cacheValid &&
          cachedSearchQueryRef.current === debouncedSearchQuery &&
          cachedRecords.length > 0

        if (canRestoreFromCache) {
          // Restore from cache instead of fetching
          // Set flag to prevent fetchMoreRecords from being called during scroll restoration
          isRestoringCacheRef.current = true
          setRecords(cachedRecords)
          currentPageRef.current = cachedCurrentPage
          setCurrentPage(cachedCurrentPage)
          setHasMorePages(true) // Assume there might be more pages
          // Restore scroll position after render, then clear the restoring flag
          requestAnimationFrame(() => {
            if (optionsRef.current) {
              optionsRef.current.scrollTop = cachedScrollPosition
            }
            // Clear flag after scroll position is restored (use setTimeout to ensure scroll event has been processed)
            setTimeout(() => {
              isRestoringCacheRef.current = false
            }, 0)
          })
        } else {
          // Fresh fetch - reset pagination state
          currentPageRef.current = 1
          setCurrentPage(1)
          setHasMorePages(true)
          setRecords([])
          fetchRecords(debouncedSearchQuery)
        }
      }
    }
  }, [
    isOpen,
    debouncedSearchQuery,
    fetchRecords,
    minChars,
    cacheValid,
    cachedRecords,
    cachedCurrentPage,
    cachedScrollPosition,
  ])

  // Reset search and pagination when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSearchOperator('startsWith')
      currentPageRef.current = 1
      setCurrentPage(1)
      setHasMorePages(true)
      setIsLoadingMore(false)
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [isOpen])

  // Track previous value to detect value prop changes (initialized to undefined to detect initial value)
  const previousValueRef = useRef<string | null | undefined>(undefined)

  // Ref for fetchAndSetRecordById to avoid effect re-runs
  const fetchAndSetRecordByIdRef = useRef(fetchAndSetRecordById)
  fetchAndSetRecordByIdRef.current = fetchAndSetRecordById

  // Request ID to handle React 18 Strict Mode and race conditions
  const valueSyncRequestIdRef = useRef(0)

  // Sync external value prop with internal selectedRecord state (controlled mode) —
  // SINGLE-select only, bails out immediately for isMultiple (see the array-valued
  // effect right below). Only depends on value - uses refs for callbacks to avoid re-runs
  useEffect(() => {
    if (isMultiple) return
    const singleValue = value as string | null

    // On first run, previousValueRef is undefined, so we should process the initial value
    const isInitialRun = previousValueRef.current === undefined
    const valueChanged = isInitialRun || previousValueRef.current !== singleValue
    previousValueRef.current = singleValue

    // value null/undefined/'' means "no selection" in controlled mode — clear the visual state.
    // LineFormModal resetea los searchers a '' (no null), así que tratamos vacío igual que null
    // para evitar un fetch por Id: '' que deja el valor anterior pintado.
    if (singleValue === null || singleValue === undefined || singleValue === '') {
      // Invalida cualquier fetch(idAnterior) en vuelo: el .then de ese fetch
      // chequea isStale contra este request-id, así que al incrementarlo aquí
      // su resultado se descarta y no repinta el registro viejo (race del
      // remontaje del modal: value pasa a '' mientras el fetch del id anterior
      // aún no resuelve).
      ++valueSyncRequestIdRef.current
      setSelectedRecords([])
      setIsInvalidValue(false)
      return
    }

    // If value didn't change, skip
    if (!valueChanged) {
      return
    }

    // Si el registro ya está en memoria (lo acaba de seleccionar el usuario),
    // no re-fetchar: evita el parpadeo de "[record not found]"
    const currentRecord = selectedRecordRef.current
    if (currentRecord) {
      const recordId = String(currentRecord['Id'] ?? currentRecord['id'] ?? '')
      if (recordId === singleValue) {
        setIsInvalidValue(false)
        return
      }
    }

    // Increment request ID to invalidate any pending requests
    const currentRequestId = ++valueSyncRequestIdRef.current

    fetchAndSetRecordByIdRef.current(singleValue).then((record) => {
      const isStale = valueSyncRequestIdRef.current !== currentRequestId
      if (isStale) return
      if (record) {
        setSelectedRecords([record])
        setIsInvalidValue(false)
      } else {
        // Record not found - invalid value
        setIsInvalidValue(true)
        setSelectedRecords([])
        onInvalidValueRef.current?.(singleValue)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiple, value])

  // Ref for fetchRecordsByIds to avoid effect re-runs
  const fetchRecordsByIdsRef = useRef(fetchRecordsByIds)
  fetchRecordsByIdsRef.current = fetchRecordsByIds

  // MULTI-select equivalent of the effect above — batches the whole `value` array into
  // one fetch instead of one fetch per id. Bails out immediately for single-select.
  const previousMultiValueKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!isMultiple) return
    const ids = (value as string[] | undefined) ?? []
    const idsKey = JSON.stringify(ids)

    if (previousMultiValueKeyRef.current === idsKey) return
    previousMultiValueKeyRef.current = idsKey

    if (ids.length === 0) {
      setSelectedRecords([])
      setIsInvalidValue(false)
      return
    }

    // Skip refetch when every id is already in memory (the user just picked them from
    // the dropdown) — same "avoid the not-found flicker" guard as single-select above.
    const currentIds = new Set(selectedRecordsRef.current.map((r) => String(r.Id)))
    if (ids.every((recordId) => currentIds.has(recordId))) return

    const currentRequestId = ++valueSyncRequestIdRef.current
    fetchRecordsByIdsRef.current(ids).then((resolved) => {
      const isStale = valueSyncRequestIdRef.current !== currentRequestId
      if (isStale) return
      setSelectedRecords(resolved)
      setIsInvalidValue(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiple, JSON.stringify(value)])

  // Dependencies-based reload (AC: 7, 8)
  const dependenciesKey = JSON.stringify(dependencies ?? [])
  useEffect(() => {
    if (previousDependenciesRef.current !== dependenciesKey) {
      previousDependenciesRef.current = dependenciesKey

      // Abort any pending requests to prevent race conditions
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Invalidate cache on dependencies change (Story 2.13, AC: 6)
      setCacheValid(false)

      // Reset state on dependency change
      setRecords([])
      setSelectedRecords([])
      currentPageRef.current = 1
      setCurrentPage(1)
      setHasMorePages(true)
      setIsOpen(false)
      setSearchQuery('')
      // Note: Don't trigger fetch here - it will happen when dropdown opens
    }
  }, [dependenciesKey])

  // Invalidate cache on filters change (Story 2.13, AC: 4)
  const previousFiltersRef = useRef(filtersKey)
  useEffect(() => {
    if (previousFiltersRef.current !== filtersKey) {
      previousFiltersRef.current = filtersKey
      setCacheValid(false)
    }
  }, [filtersKey])

  // Invalidate cache on search operator change — otherwise toggling startsWith/contains
  // with the same search text would restore the old (wrong-operator) cached results
  // instead of doing a fresh fetch.
  const previousSearchOperatorRef = useRef(searchOperator)
  useEffect(() => {
    if (previousSearchOperatorRef.current !== searchOperator) {
      previousSearchOperatorRef.current = searchOperator
      setCacheValid(false)
    }
  }, [searchOperator])

  /**
   * Gets display text for the selected value in the trigger.
   * Implements priority order: renderSelected > displayTemplate > displayValue > all displayFields concatenated
   */
  const getSelectedDisplay = useCallback(
    (record: TRecord): string => {
      // Priority 1: renderSelected function
      if (renderSelected) {
        return renderSelected(record)
      }

      // Priority 2: displayTemplate with interpolation
      if (displayTemplate) {
        return interpolateTemplate(displayTemplate, record)
      }

      // Priority 3: single displayValue field
      if (displayValue) {
        const value = record[displayValue]
        return value !== undefined && value !== null ? String(value) : ''
      }

      // Default: concatenate all displayFields separated by " - "
      return displayFields
        .map((field) => {
          const value = record[field]
          return value !== undefined && value !== null ? String(value) : ''
        })
        .join(' - ')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderSelected, displayTemplate, displayValue, displayFieldsKey]
  )

  const handleTriggerClick = () => {
    if (disabled) return // Check disabled FIRST (Story 2.14, AC: 8)
    if (readOnly) return
    setIsOpen((prev) => !prev)
  }

  // The "+" button sits inside componentRef (same as the trigger), so clicking it
  // doesn't fire useClickOutside's close-on-outside-click logic — without this, the
  // dropdown (if open) stays open underneath the create popup until the user clicks
  // away a second time. Close it explicitly before opening the popup.
  const handleCreateClick = () => {
    setIsOpen(false)
    setSearchQuery('')
    setIsCreateOpen(true)
  }

  /**
   * Gets display text for dropdown option items.
   * Always uses displayFields (separated by " - ") regardless of display props.
   */
  const getOptionDisplay = (record: TRecord): string => {
    return displayFields.map((field) => String(record[field] ?? '')).join(' - ')
  }

  const handleSelectRecord = (record: TRecord) => {
    if (isMultiple) {
      const prevRecords = selectedRecordsRef.current
      const alreadySelected = prevRecords.some((r) => r.Id === record.Id)
      if (!alreadySelected && maxSelected !== undefined && prevRecords.length >= maxSelected) {
        onMaxSelectedReached?.(maxSelected)
        return
      }
      const nextRecords = alreadySelected
        ? prevRecords.filter((r) => r.Id !== record.Id)
        : [...prevRecords, record]
      setSelectedRecords(nextRecords)
      selectedRecordsRef.current = nextRecords
      setIsInvalidValue(false)

      if (validationError) {
        setValidationError(false)
        onValidationChangeRef.current?.(true)
      }

      setCachedRecords(records)
      setCachedScrollPosition(optionsRef.current?.scrollTop ?? 0)
      setCachedCurrentPage(currentPage)
      cachedSearchQueryRef.current = searchQuery
      setCacheValid(true)

      ;(onChange as (nextRecords: TRecord[], previousRecords: TRecord[]) => void)(nextRecords, prevRecords)
      setSearchQuery('')
      // Dropdown stays OPEN in multi-select — the user typically picks several before
      // closing it themselves (Escape / outside click / the trigger again).
      const toggleAnnouncementKey = alreadySelected ? 'announcements.cleared' : 'announcements.selected'
      setAnnouncement(t(toggleAnnouncementKey, { value: getSelectedDisplay(record) }))
      return
    }

    previousRecordRef.current = selectedRecord
    setSelectedRecords([record])
    selectedRecordRef.current = record // Update ref immediately for blur callbacks
    setIsInvalidValue(false) // Clear invalid state on valid selection

    // Clear validation error on selection (Story 2.12, AC: 9)
    if (validationError) {
      setValidationError(false)
      onValidationChangeRef.current?.(true)
    }

    // Save navigation context cache before closing (Story 2.13, AC: 2, 3)
    setCachedRecords(records)
    setCachedScrollPosition(optionsRef.current?.scrollTop ?? 0)
    setCachedCurrentPage(currentPage)
    cachedSearchQueryRef.current = searchQuery
    setCacheValid(true)

    ;(onChange as (record: TRecord | null, previousRecord: TRecord | null) => void)(
      record,
      previousRecordRef.current
    )
    setSearchQuery('')
    setIsOpen(false)
    setApiError(null)
    // Return focus to trigger after selection (fixes Tab behavior)
    // Set flag to prevent handleTriggerFocus from reopening dropdown
    justSelectedRef.current = true
    triggerRef.current?.focus({ preventScroll: true })
    // Announce selection
    const displayText = getSelectedDisplay(record)
    setAnnouncement(t('announcements.selected', { value: displayText }))
  }

  // Fired by the host's create form (via onCreateNew's `onCreated` helper)
  // after successfully creating a new record. Always selects it exactly like picking
  // it from the dropdown (fires onChange) and closes the create popup. Plain function
  // (not memoized) — same pattern as handleSelectRecord/handleClear above, which it wraps.
  const handleRecordCreated = (record: TRecord) => {
    handleSelectRecord(record)
    setIsCreateOpen(false)
  }

  // Enter pressed with nothing highlighted: the user typed a full code and wants it
  // resolved directly, without arrowing down to a startsWith/contains match first (or
  // one may not even be visible yet — the debounce might not have fired, or the exact
  // record might not be on the currently loaded page). Runs a dedicated `equals` lookup
  // — the only place this codebase ever sends `searchOperator: 'equals'` — rather than
  // trusting the current startsWith/contains results, which could pick the wrong record
  // (e.g. code "A1" also prefix/substring-matches "A10").
  const handleExactMatchEnter = async (query: string) => {
    const trimmed = query.trim()
    if (!trimmed || isResolvingExactMatchRef.current) return
    isResolvingExactMatchRef.current = true
    setExactMatchStatus('searching')
    setAnnouncement(t('announcements.exactMatchResolving', { value: trimmed }))

    try {
      const allFields = [...new Set(['Id', ...displayFields, ...(bindFields ?? [])])]
      const response = await fetcher<TRecord>(entity, {
        fields: allFields,
        search: trimmed,
        searchFields: searchFields ?? displayFields,
        searchOperator: 'equals',
        page: 1,
        pageSize: 1,
        filters: getEffectiveFilters(),
        orderBy: effectiveOrderBy,
      })

      const match = response.data[0]
      setExactMatchStatus('idle')
      if (match) {
        handleSelectRecord(match)
      } else {
        // Nothing visible on purpose — no red banner, just silence. Screen readers
        // still get the outcome via the announcement.
        setAnnouncement(t('announcements.exactMatchNotFound', { value: trimmed }))
      }
    } catch (err) {
      setExactMatchStatus('idle')
      const error = err instanceof Error ? err : new Error('Unknown error')
      onError?.(error)
    } finally {
      isResolvingExactMatchRef.current = false
    }
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    const prevRecords = selectedRecordsRef.current
    previousRecordRef.current = prevRecords[0] ?? null
    setSelectedRecords([])
    selectedRecordRef.current = null // Update ref immediately
    selectedRecordsRef.current = []
    setIsInvalidValue(false)
    if (isMultiple) {
      ;(onChange as (records: TRecord[], previousRecords: TRecord[]) => void)([], prevRecords)
    } else {
      ;(onChange as (record: TRecord | null, previousRecord: TRecord | null) => void)(null, prevRecords[0] ?? null)
    }
    setAnnouncement(t('announcements.cleared'))
  }

  // Keyboard-triggered clear (no event needed)
  const handleKeyboardClear = useCallback(() => {
    if (selectedRecordsRef.current.length === 0 && !isInvalidValue) return
    const prevRecords = selectedRecordsRef.current
    previousRecordRef.current = prevRecords[0] ?? null
    setSelectedRecords([])
    selectedRecordRef.current = null // Update ref immediately
    selectedRecordsRef.current = []
    setIsInvalidValue(false)
    if (isMultiple) {
      ;(onChange as (records: TRecord[], previousRecords: TRecord[]) => void)([], prevRecords)
    } else {
      ;(onChange as (record: TRecord | null, previousRecord: TRecord | null) => void)(null, prevRecords[0] ?? null)
    }
    setAnnouncement(t('announcements.cleared'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalidValue, isMultiple, onChange, t])

  // Close handler for keyboard navigation (Tab)
  // Finds the next tabbable element outside the component and focuses it
  const handleClose = useCallback(() => {
    // Set flag to prevent trigger from reopening dropdown when it receives focus during Tab navigation
    isTabNavigatingRef.current = true

    // Close the dropdown
    setIsOpen(false)
    setSearchQuery('')
    setAnnouncement(t('announcements.closed'))

    // Find and focus the next tabbable element after the component
    // This ensures Tab navigation works correctly even when the dropdown is unmounted
    // Use setTimeout to ensure this runs after React's state update and re-render
    setTimeout(() => {
      if (!componentRef.current) return

      // Get all tabbable elements in the document (in DOM order)
      const tabbableSelector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      const allTabbable = Array.from(document.querySelectorAll<HTMLElement>(tabbableSelector))

      // Find the index of our component's trigger button in the tab order
      const componentIndex = allTabbable.findIndex((el) => componentRef.current?.contains(el))

      // Find the first tabbable element AFTER the component (not inside it)
      let nextElement: HTMLElement | null = null
      for (let i = componentIndex + 1; i < allTabbable.length; i++) {
        if (!componentRef.current?.contains(allTabbable[i])) {
          nextElement = allTabbable[i]
          break
        }
      }

      if (nextElement) {
        nextElement.focus({ preventScroll: true })
      }
    }, 0)
  }, [t])

  // Escape handler - close AND return focus to trigger
  const handleEscapeClose = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setAnnouncement(t('announcements.closed'))
    triggerRef.current?.focus({ preventScroll: true })
  }, [t])

  // Imperative clear (no event needed, for ref.clear())
  const handleImperativeClear = useCallback(() => {
    const prevRecords = selectedRecordsRef.current
    previousRecordRef.current = prevRecords[0] ?? null
    setSelectedRecords([])
    selectedRecordsRef.current = []
    setIsInvalidValue(false)
    if (isMultiple) {
      ;(onChange as (records: TRecord[], previousRecords: TRecord[]) => void)([], prevRecords)
    } else {
      ;(onChange as (record: TRecord | null, previousRecord: TRecord | null) => void)(null, prevRecords[0] ?? null)
    }
    setAnnouncement(t('announcements.cleared'))
  }, [isMultiple, onChange, t])

  // Create imperative ref object (uses refs to always get latest values). Note: in
  // multi-select mode getValue()/setValue() only operate on a single record — see the
  // doc comments on LookupFieldRef for the exact multi-mode semantics.
  const imperativeRef = useMemo<LookupFieldRef<TRecord>>(
    () => ({
      clear: handleImperativeClear,
      focus: () => triggerRef.current?.focus({ preventScroll: true }),
      reload: () => fetchRecords(debouncedSearchQuery),
      getValue: () => selectedRecordRef.current,
      setValue: async (recordId: string) => {
        const record = await fetchAndSetRecordById(recordId)
        if (record) {
          const prevRecords = selectedRecordsRef.current
          previousRecordRef.current = prevRecords[0] ?? null
          setSelectedRecords([record])
          setIsInvalidValue(false)
          if (isMultiple) {
            ;(onChange as (records: TRecord[], previousRecords: TRecord[]) => void)([record], prevRecords)
          } else {
            ;(onChange as (r: TRecord | null, prev: TRecord | null) => void)(record, previousRecordRef.current)
          }
        } else {
          // Record not found - set invalid state and notify
          setIsInvalidValue(true)
          setSelectedRecords([])
          onInvalidValue?.(recordId)
        }
      },
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      validate: performValidation,
    }),
    [
      handleImperativeClear,
      fetchRecords,
      debouncedSearchQuery,
      fetchAndSetRecordById,
      isMultiple,
      onChange,
      onInvalidValue,
      performValidation,
    ]
  )

  // Expose ref to parent
  useImperativeHandle(ref, () => imperativeRef, [imperativeRef])

  // Keyboard navigation hook
  const { highlightedIndex, setHighlightedIndex, handleKeyDown } = useKeyboardNavigation({
    records: displayRecords,
    isOpen,
    onSelect: handleSelectRecord,
    onClose: handleClose,
    onEscape: handleEscapeClose,
    onClear: handleKeyboardClear,
    searchQuery,
    onEnterWithoutHighlight: handleExactMatchEnter,
  })

  // Reset highlightedIndex when dropdown opens or records change. When the current
  // search narrows to exactly one match, highlight it automatically — it reads as
  // "already selected" for the user, and lets Enter confirm it immediately through the
  // normal highlighted-Enter path instead of falling through to the exact-match lookup.
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(displayRecords.length === 1 ? 0 : -1)
    }
  }, [isOpen, displayRecords, setHighlightedIndex])

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedOption = optionsRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`
      ) as HTMLElement
      if (highlightedOption?.scrollIntoView) {
        highlightedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [highlightedIndex])

  // Track if focus came from mouse click (to distinguish Tab vs Click)
  const wasClickedRef = useRef(false)
  // Track if focus is from programmatic selection (to prevent dropdown reopening)
  const justSelectedRef = useRef(false)
  // Track if Tab navigation is in progress (to prevent dropdown reopening)
  const isTabNavigatingRef = useRef(false)

  const handleTriggerMouseDown = () => {
    wasClickedRef.current = true
  }

  // Handle keydown on trigger (when dropdown is closed)
  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    // Block keyboard when disabled (Story 2.14, AC: 6)
    if (disabled) return

    // The trigger renders as a <div role="button"> (see the render below for why),
    // which — unlike a native <button> — doesn't get Enter/Space-opens-it for free
    // from the browser.
    if (!isOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      handleTriggerClick()
      return
    }

    // Only handle when dropdown is closed
    if (isOpen) return

    // Backspace clears selection when clearable is enabled, not readOnly, and there's a value
    if (event.key === 'Backspace' && clearable && !readOnly && (selectedRecord || isInvalidValue)) {
      event.preventDefault()
      handleKeyboardClear()
    }
  }

  // Handle focus on trigger to open dropdown (Tab key behavior - AC1)
  const handleTriggerFocus = () => {
    if (disabled) return // Block focus handling when disabled (Story 2.14)
    if (readOnly) return
    // Skip opening if focus came from programmatic selection
    if (justSelectedRef.current) {
      justSelectedRef.current = false
      return
    }
    // Skip opening if Tab navigation is closing the dropdown
    if (isTabNavigatingRef.current) {
      isTabNavigatingRef.current = false
      return
    }
    // Only open on keyboard focus (Tab), not on click-focus
    if (wasClickedRef.current) {
      wasClickedRef.current = false
      return
    }
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  // Handle component-level focus (fires once when focus enters component)
  const handleComponentFocus = useCallback(() => {
    // Skip focus callbacks when disabled (Story 2.14, AC: 1)
    if (disabled) return
    // Only fire onFocus if focus is entering the component (not moving within)
    if (!isFocusWithinRef.current) {
      isFocusWithinRef.current = true
      // Use ref to get latest selectedRecord (avoids stale closure)
      onFocusProp?.(selectedRecordRef.current)
    }
  }, [onFocusProp, disabled])

  // Handle component-level blur (fires once when focus leaves component entirely)
  const handleComponentBlur = useCallback(
    (event: React.FocusEvent) => {
      // Skip blur callbacks when disabled (Story 2.14, AC: 1)
      if (disabled) return

      // Check if focus is moving outside the component
      const relatedTarget = event.relatedTarget as Node | null
      const isLeavingComponent = !componentRef.current?.contains(relatedTarget)

      if (isLeavingComponent) {
        isFocusWithinRef.current = false

        // Perform validation on blur if enabled (Story 2.12, AC: 3, 4, 10)
        if (validateOnBlur && !readOnly) {
          performValidation()
        }

        // Use ref to get latest selectedRecord (avoids stale closure)
        onBlurProp?.(selectedRecordRef.current)
      }
    },
    [onBlurProp, validateOnBlur, readOnly, disabled, performValidation]
  )

  const renderDropdownContent = () => {
    // Show min chars message when user has typed but fewer than minChars
    if (searchQuery.length > 0 && searchQuery.length < minChars) {
      return <div className="lookup-field__empty">{t('minChars', { count: minChars })}</div>
    }

    if (isLoading) {
      return (
        <div className="lookup-field__loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('loading')}</span>
        </div>
      )
    }

    if (apiError) {
      return <div className="lookup-field__error">{getFriendlyErrorMessage(apiError, t)}</div>
    }

    if (displayRecords.length === 0) {
      return <div className="lookup-field__empty">{noResultsText ?? t('noResults')}</div>
    }

    // Fallback to non-virtualized rendering when virtualizer can't calculate items
    // (happens in test environments where container has no dimensions)
    const useVirtualizedRendering = virtualItems.length > 0

    const isOptionSelected = (record: TRecord) =>
      isMultiple ? selectedRecords.some((r) => r.Id === record.Id) : selectedRecord?.Id === record.Id
    const isOptionMaxDisabled = (record: TRecord) =>
      isMultiple && maxSelected !== undefined && selectedRecords.length >= maxSelected && !isOptionSelected(record)

    const renderOptionContent = (record: TRecord) => (
      <>
        {isMultiple && (
          <span
            className={cn(
              'lookup-field__option-check',
              isOptionSelected(record) && 'lookup-field__option-check--checked'
            )}
            aria-hidden="true"
          >
            {isOptionSelected(record) && <Check className="w-3.5 h-3.5" />}
          </span>
        )}
        {getOptionDisplay(record)}
      </>
    )

    return (
      <div ref={optionsRef} className="lookup-field__options" onScroll={handleScroll}>
        {useVirtualizedRendering ? (
          // Virtualized list container (Story 2.13, AC: 1, 8)
          <div
            style={{
              height: `${totalSize}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualItem) => {
              const record = displayRecords[virtualItem.index]
              const index = virtualItem.index
              return (
                <div
                  key={record.Id}
                  id={`${id}-option-${record.Id}`}
                  data-index={index}
                  className={cn(
                    'lookup-field__option',
                    index === highlightedIndex && 'lookup-field__option--highlighted',
                    isOptionSelected(record) && 'lookup-field__option--selected',
                    isOptionMaxDisabled(record) && 'lookup-field__option--max-disabled'
                  )}
                  role="option"
                  aria-selected={isOptionSelected(record)}
                  aria-disabled={isOptionMaxDisabled(record) || undefined}
                  tabIndex={-1}
                  onClick={() => handleSelectRecord(record)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {renderOptionContent(record)}
                </div>
              )
            })}
          </div>
        ) : (
          // Non-virtualized fallback for test environments
          displayRecords.map((record, index) => (
            <div
              key={record.Id}
              id={`${id}-option-${record.Id}`}
              data-index={index}
              className={cn(
                'lookup-field__option',
                index === highlightedIndex && 'lookup-field__option--highlighted',
                isOptionSelected(record) && 'lookup-field__option--selected',
                isOptionMaxDisabled(record) && 'lookup-field__option--max-disabled'
              )}
              role="option"
              aria-selected={isOptionSelected(record)}
              aria-disabled={isOptionMaxDisabled(record) || undefined}
              tabIndex={-1}
              onClick={() => handleSelectRecord(record)}
            >
              {renderOptionContent(record)}
            </div>
          ))
        )}
        {/* Loading more spinner at bottom (AC: 3) */}
        {isLoadingMore && (
          <div className="lookup-field__loading-more">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {/* No end indicator per FR30 (AC: 6) */}
      </div>
    )
  }

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | HTMLDivElement | null) => {
      // Combine refs: triggerRef for internal use, floatingRefs.setReference for positioning
      triggerRef.current = node
      floatingRefs.setReference(node)
    },
    [floatingRefs]
  )

  return (
    <div
      ref={componentRef}
      className={cn(
        'lookup-field flex flex-col',
        disabled && 'lookup-field--disabled',
        readOnly && 'lookup-field--readonly',
        showError && 'lookup-field--error',
        className
      )}
      role="combobox"
      aria-expanded={!disabled && isOpen}
      aria-haspopup="listbox"
      aria-labelledby={`${id}-label`}
      aria-controls={isOpen ? `${id}-listbox` : undefined}
      aria-disabled={disabled || undefined}
      aria-readonly={(!disabled && readOnly) || undefined}
      aria-invalid={showError || undefined}
      aria-required={required || undefined}
      onKeyDown={disabled ? undefined : handleKeyDown}
      onFocus={handleComponentFocus}
      onBlur={handleComponentBlur}
    >
      <LiveRegion message={announcement} />
      <label id={`${id}-label`} htmlFor={id} className="lookup-field__label">
        {label}
        {effectiveShowRequiredIndicator && (
          <span className="lookup-field__required-indicator">
            {t('validation.requiredIndicator')}
          </span>
        )}
      </label>
      <div className={showCreateButton ? 'lookup-field__row' : undefined}>
        {/* Always a <div role="button"> rather than a native <button> — both modes can
            end up with a real <button> among its own children (multi-select's chip
            "remove" controls; single-select's own clear "x"), and a <button> can't
            legally contain another <button> in the HTML content model. createElement
            (rather than a JSX tag) sidesteps the ref-type friction of a prop-polymorphic
            element; handleTriggerKeyDown adds the Enter/Space-opens-it behavior a native
            button gets for free but a div role="button" does not. */}
        {createElement(
          'div',
          {
            ref: setTriggerRef,
            id,
            className: cn('lookup-field__trigger', isMultiple && 'lookup-field__trigger--multiple'),
            onClick: handleTriggerClick,
            onMouseDown: handleTriggerMouseDown,
            onKeyDown: handleTriggerKeyDown,
            onFocus: handleTriggerFocus,
            tabIndex: disabled ? -1 : 0,
            role: 'button',
            'aria-disabled': disabled || undefined,
          },
          isMultiple && selectedRecords.length > 0 ? (
            <span className="lookup-field__chips">
              {selectedRecords.slice(0, MAX_VISIBLE_CHIPS).map((record) => (
                <span key={record.Id} className="lookup-field__chip">
                  <Badge color="primary" label={getSelectedDisplay(record)} />
                  {!readOnly && !disabled && (
                    <button
                      type="button"
                      className="lookup-field__chip-remove"
                      aria-label={removeLabel}
                      onClick={(event) => {
                        event.stopPropagation()
                        handleSelectRecord(record)
                      }}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </span>
              ))}
              {selectedRecords.length > MAX_VISIBLE_CHIPS && (
                <Badge color="zinc" label={`+${selectedRecords.length - MAX_VISIBLE_CHIPS}`} />
              )}
            </span>
          ) : (
            <span className="lookup-field__selected-text">
              {isInvalidValue
                ? t('notFound')
                : selectedRecord
                  ? getSelectedDisplay(selectedRecord)
                  : (placeholder ?? t('placeholder'))}
            </span>
          ),
          <span
            className={cn(
              'lookup-field__icon-btn lookup-field__chevron',
              isOpen && 'lookup-field__chevron--open'
            )}
          >
            <ChevronDown className="lookup-field__icon-btn-glyph" aria-hidden="true" />
          </span>,
          clearable && (isMultiple ? selectedRecords.length > 0 : selectedRecord) && !readOnly && !disabled && (
            <button
              type="button"
              className="lookup-field__icon-btn lookup-field__icon-btn--interactive lookup-field__clear"
              onClick={handleClear}
              aria-label={clearSelectionLabel}
            >
              <X className="lookup-field__icon-btn-glyph" aria-hidden="true" />
            </button>
          )
        )}
        {showCreateButton && (
          <button
            type="button"
            className="lookup-field__icon-btn lookup-field__icon-btn--interactive lookup-field__create-btn"
            onClick={handleCreateClick}
            disabled={disabled || readOnly}
            aria-label={createButtonLabel ?? t('createNew')}
            title={createButtonLabel ?? t('createNew')}
          >
            <Plus className="lookup-field__icon-btn-glyph" aria-hidden="true" />
          </button>
        )}
      </div>
      {onCreateNew && isCreateOpen && (
        <CreatePopup
          variant={createVariant ?? 'modal'}
          title={createTitle ?? t('createNew')}
          icon={createIcon}
          width={createWidth}
          height={createHeight}
          showHeader={createShowHeader}
          onClose={() => setIsCreateOpen(false)}
        >
          {onCreateNew({
            onCreated: handleRecordCreated,
            onCancel: () => setIsCreateOpen(false),
          })}
        </CreatePopup>
      )}
      {errorText && (
        <span className="lookup-field__error-text" role="alert">
          {errorText}
        </span>
      )}
      {isOpen && createPortal(
        <div
          ref={floatingRefs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 9999, // Ensure it's above modals
          }}
          className="lookup-field__dropdown"
        >
          <SearchInput
            ref={searchInputRef}
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value)
              if (exactMatchStatus !== 'idle') setExactMatchStatus('idle')
            }}
            placeholder={searchPlaceholder ?? t('searchPlaceholder')}
            searchOperator={searchOperator}
            onSearchOperatorChange={setSearchOperator}
            startsWithLabel={t('searchOperatorStartsWith')}
            containsLabel={t('searchOperatorContains')}
            exactMatchStatus={exactMatchStatus}
            listboxId={`${id}-listbox`}
            activeDescendantId={
              highlightedIndex >= 0 && displayRecords[highlightedIndex]
                ? `${id}-option-${displayRecords[highlightedIndex].Id}`
                : undefined
            }
          />
          <div id={`${id}-listbox`} role="listbox" aria-label={label}>
            {renderDropdownContent()}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// Export with forwardRef for generic component support
export const LookupField = forwardRef(LookupFieldInner) as <
  TRecord extends LookupRecord = LookupRecord,
>(
  props: LookupFieldProps<TRecord> & { ref?: React.Ref<LookupFieldRef<TRecord>> }
) => React.ReactElement

// Set displayName for debugging
;(LookupField as React.FC).displayName = 'LookupField'
